import * as React from 'react';
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from '@material-ui/core/styles';
import { DeltaXMLColors } from '../styling/DeltaColors';
import { AppBar, Button, Dialog, DialogActions, DialogContent, Grid, IconButton, TextField, Toolbar, Tooltip, Typography } from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import { AuthContext } from './AuthContext';

const styles = (theme: Theme) =>
  createStyles({
    appBar: {
      alignItems: "left",
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
      color: DeltaXMLColors.CELERY
    },
    copiedCheckmarkIcon: {
      marginTop: '6px',
      marginLeft: '5px'
    }
  });

interface TokenDialogProps extends WithStyles<typeof styles> {
  isDialogOpened: boolean;
  handleDialogClose: () => void;
  download(): void;
}

function TokenDialog(props: TokenDialogProps) {
  const { classes, isDialogOpened, handleDialogClose, download } = props;
  const auth = React.useContext(AuthContext);

  const [isTokenIdCopied, setTokenIdCopied] = React.useState(false);
  const [isRefreshTokenCopied, setRefreshTokenCopied] = React.useState(false);
  const [isTokenExpirationCopied, setTokenExpirationCopied] = React.useState(false);

  const copyIdToken = () => {
    navigator.clipboard.writeText(auth.authVariables?.id_token as string);
    setTokenIdCopied(true);
    setTimeout(() => {
      setTokenIdCopied(false);
    }, 4000);
  }

  const copyRefreshToken = () => {
    navigator.clipboard.writeText(auth.authVariables?.refresh_token as string);
    setRefreshTokenCopied(true);
    setTimeout(() => {
      setRefreshTokenCopied(false);
    }, 4000);
  }

  const copyExpirationToken = () => {
    navigator.clipboard.writeText(auth.authVariables?.expires_at as string);
    setTokenExpirationCopied(true);
    setTimeout(() => {
      setTokenExpirationCopied(false);
    }, 4000);
  }

  return (
    <Dialog open={isDialogOpened} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <AppBar className={classes.appBar} position="static">
        <Toolbar>
          <Typography variant="h4">DeltaJSON Token</Typography>
        </Toolbar>
      </AppBar>
      <br />
      <DialogContent>
        <Grid container direction="column" xs={12}>
          <Grid item xs={12}>
            <Typography variant='h5' color='primary'>
              Token ID
            </Typography>
            <Grid container direction="row" xs={12} spacing={1}>
              <Grid item xs={11}>
                <TextField variant='outlined' size="small" defaultValue={auth.authVariables?.id_token}
                  fullWidth={true}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={1}>
                {!isTokenIdCopied ?
                  <Tooltip title="Copy" aria-label="copy" placement='top' arrow>
                    <IconButton onClick={copyIdToken}>
                      <FileCopyOutlinedIcon fontSize='small' color='primary' />
                    </IconButton>
                  </Tooltip>
                  :
                  <Tooltip title="Copied!" aria-label="copied" placement='top' leaveDelay={4000} arrow>
                    <CheckIcon fontSize='default' color='primary' className={classes.copiedCheckmarkIcon} />
                  </Tooltip>
                }
              </Grid>
            </Grid>
          </Grid>
          <br />
          <Grid item xs={12}>
            <Typography variant='h5' color='primary'>
              Refresh Token
            </Typography>
            <Grid container direction="row" xs={12} spacing={1}>
              <Grid item xs={11}>
                <TextField variant='outlined' size="small" defaultValue={auth.authVariables?.refresh_token}
                  fullWidth={true}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={1}>
                {!isRefreshTokenCopied ?
                  <Tooltip title="Copy" aria-label="copy" placement='top' arrow>
                    <IconButton onClick={copyRefreshToken}>
                      <FileCopyOutlinedIcon fontSize='small' color='primary' />
                    </IconButton>
                  </Tooltip>
                  :
                  <Tooltip title="Copied!" aria-label="copied" placement='top' leaveDelay={4000} arrow>
                    <CheckIcon fontSize='default' color='primary' className={classes.copiedCheckmarkIcon} />
                  </Tooltip>
                }
              </Grid>
            </Grid>
          </Grid>
          <br />
          <Grid item xs={12}>
            <Typography variant='h5' color='primary'>
              Token Expiration Date
            </Typography>
            <Grid container direction="row" xs={12} spacing={1}>
              <Grid item xs={11}>
                <TextField variant='outlined' size="small" defaultValue={auth.authVariables?.expires_at}
                  fullWidth={true}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={1}>
                {!isTokenExpirationCopied ?
                  <Tooltip title="Copy" aria-label="copy" placement='top' arrow>
                    <IconButton onClick={copyExpirationToken}>
                      <FileCopyOutlinedIcon fontSize='small' color='primary' />
                    </IconButton>
                  </Tooltip>
                  :
                  <Tooltip title="Copied!" aria-label="copied" placement='top' leaveDelay={4000} arrow>
                    <CheckIcon fontSize='default' color='primary' className={classes.copiedCheckmarkIcon} />
                  </Tooltip>
                }
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <br />
      <DialogActions>
        <Button variant='contained' color='primary' onClick={download}>Download Token</Button>
        <Button variant='contained' color='primary' onClick={handleDialogClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default withStyles(styles)(TokenDialog);
